import React from 'react';
import { areaList } from '@/common/api/common';
import { TreeSelect } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deepCopy, isArray, recursionEach } from '@lhb/func';
import { changeProvinceCityDistrict, changeProvincesCities } from '../../config-v2';
import V2FormTreeSelect, { V2FormTreeSelectProps } from '../V2FormTreeSelect/V2FormTreeSelect';
import { useMethods } from '@lhb/hook';
import { V2FormContext } from '../V2Form';

export interface V2FormProvinceTreeProps extends Omit<V2FormTreeSelectProps, 'treeData'> {
  /**
   * @description value目标值
   */
  valueKey?: 'id' | 'code';
  /**
   * @description 是否多选
   */
  multiple?: boolean;
  /**
   * @description 控件类型，type(1: 省市区, 2: 省市)
   */
  type?: number;
  /**
   * @description form实例
   */
  form?: any;
  /**
   * @description 唤起时的初始值，传入此参数时，就不会通过form.get来获取初始值，仅提供给特殊场景使用，必须设置strictUseDefaultValue
   */
  defaultValue?: any;
  /**
   * @description 是否使用defaultValue模式，将停止使用form.get来获取初始值，仅提供给特殊场景使用
   */
  strictUseDefaultValue?: boolean;
}
const V2FormProvinceTree: React.FC<V2FormProvinceTreeProps> = ({
  label,
  name,
  rules,
  placeholder,
  noStyle,
  formItemConfig = {},
  onChange,
  allowClear = true,
  config = {},
  className,
  disabled,
  required,
  type = 1,
  multiple = false,
  valueKey = 'id', // 可选code
  form,
  defaultValue,
  strictUseDefaultValue = false
}) => {
  const { form: baseForm } = useContext(V2FormContext);
  const _form = form || baseForm;
  const province = useSelector((state: any) =>
    type === 1 ? state.common.provinceCityDistrict : state.common.provincesCities
  );
  const dispatch = useDispatch();
  const [newProvince, setNewProvince] = useState<any[]>([]);
  const [treeDefaultExpandedKeys, setTreeDefaultExpandedKeys] = useState<any[]>([]);
  const methods = useMethods({
    onDropdownVisibleChange(open) {
      if (open) {
        let value: any = defaultValue;
        if (!strictUseDefaultValue) {
          value = _form.getFieldValue(name);
        }
        if (isArray(value) ? value?.length : value) {
          if (multiple) {
            setTreeDefaultExpandedKeys(value.map(item => item.value));
          } else {
            setTreeDefaultExpandedKeys([value]);
          }
        } else {
          setTreeDefaultExpandedKeys([]);
        }
      }
    }
  });

  useEffect(() => {
    const getAreaList = async () => {
      const cityResult: any = await areaList({ type });
      if (type === 1) {
        dispatch(changeProvinceCityDistrict(cityResult));
      } else {
        dispatch(changeProvincesCities(cityResult));
      }
    };
    // 如果不存在则进行请求
    if (!province.length) {
      getAreaList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    const _province = deepCopy(province);
    if (province) {
      recursionEach(_province, 'children', item => {
        item.label = item.name;
        if (item.parent) {
          item.value = item.parent + ',' + item[valueKey];
          item.fullLabel = item.parentFullLabel + '/' + item.label;
        } else {
          item.value = item[valueKey] + '';
          item.fullLabel = item.label;
        }

        item.children?.map(city => {
          city.parent = item.value;
          city.parentFullLabel = item.fullLabel;
        });
      });
    }
    setNewProvince(_province);
  }, [province]);

  return (
    <V2FormTreeSelect
      label={label}
      name={name}
      treeData={newProvince}
      noStyle={noStyle}
      rules={rules}
      formItemConfig={formItemConfig}
      disabled={disabled}
      placeholder={placeholder}
      onChange={onChange}
      allowClear={allowClear}
      className={className}
      required={required}
      config={{
        showCheckedStrategy: TreeSelect.SHOW_CHILD,
        treeNodeFilterProp: 'label',
        onDropdownVisibleChange: methods.onDropdownVisibleChange,
        treeDefaultExpandedKeys,
        ...(multiple ? {
          multiple: true,
          treeCheckable: true,
          maxTagCount: 'responsive',
          treeCheckStrictly: true,
          showCheckedStrategy: TreeSelect.SHOW_ALL
        } : {}),
        ...config
      }}
    />
  );
};

export default V2FormProvinceTree;
